import React from 'react'
import { Box, Button, Modal, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import DateFilter from 'components/DateFilter'
import { ActionIcon } from 'components/Modal/ActionModal'

const TIME_RANGES = ['-6h', '-1d', '-7d', '-30d']

const TIME_RANGE_LABEL_KEYS = {
  '-6h': 'wan.graphs.time_period.last_6_hours_full',
  '-1d': 'wan.graphs.time_period.last_24_hours_full',
  '-7d': 'wan.graphs.time_period.last_7_days_full',
  '-30d': 'wan.graphs.time_period.last_30_days_full'
}

const GraphModal = ({
  open,
  onClose,
  title,
  timeRangePreset,
  isCustomRange,
  dateRange,
  onTimeRangeChange,
  onStartDateChange,
  onEndDateChange,
  onDateRangeSubmit,
  onDateRangeReset,
  children
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const buttonStyle = {
    borderRadius: '4px',
    px: '10px',
    minHeight: 28,
    height: 28,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#fff',
    color: theme.palette.grey[700],
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#F5F5F5' }
  }
  const activeButtonStyle = {
    ...buttonStyle,
    borderColor: theme.palette.primary.main,
    backgroundColor: '#EDF6FF',
    color: theme.palette.primary.main,
    '&:hover': { backgroundColor: '#E4F1FF' }
  }

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="monitoring-graph-modal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: 'calc(100vw - 24px)', md: '92vw' },
          maxWidth: 1200,
          height: { xs: 'calc(100vh - 24px)', md: 'min(680px, calc(100vh - 32px))' },
          overflow: 'auto',
          bgcolor: '#fff',
          boxShadow: 24,
          p: { xs: 2, md: 4 },
          borderRadius: 2
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
          gap={2}
          mb={2}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <ActionIcon>
              <i className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" />
            </ActionIcon>
            <Typography
              id="monitoring-graph-modal-title"
              component="h2"
              sx={{ color: '#464646', fontSize: 18, fontWeight: 500, minWidth: 0 }}
            >
              {title}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            gap={1}
            flexWrap={{ xs: 'wrap', lg: 'nowrap' }}
          >
            {TIME_RANGES.map(range => (
              <Button
                key={range}
                onClick={() => onTimeRangeChange(range)}
                sx={!isCustomRange && timeRangePreset === range ? activeButtonStyle : buttonStyle}
              >
                {t(TIME_RANGE_LABEL_KEYS[range])}
              </Button>
            ))}
            <DateFilter
              placement="left"
              onReset={onDateRangeReset}
              startDate={dateRange.start}
              endDate={dateRange.end}
              onStartDateChange={onStartDateChange}
              onEndDateChange={onEndDateChange}
              onSubmit={onDateRangeSubmit}
              placeholderKey="wan.filters.date_range"
              showPresets
              blockFutureDates
              inputSx={{
                border: `1px solid ${theme.palette.border.primary}`,
                borderRadius: '4px',
                height: 28,
                minWidth: 235,
                '& .MuiInputBase-root': { height: 28 },
                '& .MuiInputBase-input': { padding: '4px 10px', fontSize: 12 }
              }}
            />
            <Button
              onClick={onClose}
              aria-label={t('actions.close')}
              sx={{
                minWidth: 28,
                minHeight: 28,
                p: 0,
                borderRadius: '50%',
                color: '#464646',
                flexShrink: 0,
                '&:hover': { backgroundColor: '#F0F0F0' }
              }}
            >
              <i className="fa-regular fa-xmark" />
            </Button>
          </Stack>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {children}
        </Box>
      </Box>
    </Modal>
  )
}

export default GraphModal
